import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { ApiError, OfflineError, apiFetchMultipart } from "@/lib/api";
import { issueKeys, projectKeys, recordKeys } from "@/lib/query-keys";
import { invalidateReportContents } from "@/lib/invalidate-report-contents";
import { createSemaphore } from "@/lib/semaphore";
import type {
  IssueContributor,
  IssueInviteLink,
  IssueUpdate,
  ProjectIssue,
  SelectableIssueStatus,
} from "@/types/api";

interface RaiseIssueVars {
  projectId: string;
  recordId: string;
  title: string;
  description?: string;
  assignedToId?: string | null;
  dueDate?: string | null;
  /**
   * Client-generated stable id for this raise. The server treats a repeat as
   * the same issue and returns the existing one, so a retry after a lost
   * response cannot leave two issues behind. The in-flight disabled button
   * covers an ordinary double-tap; this covers the bad-connection case it
   * cannot see.
   */
  clientIssueUuid?: string;
}

/** One issue in a batch raise: the record it hangs on and its own title. */
export interface BatchIssue {
  recordId: string;
  title: string;
  /**
   * Minted once per issue per opening of the sheet and reused on retry, for
   * the same reason as {@link RaiseIssueVars.clientIssueUuid} — a batch on a
   * bad connection loses responses more often than a single raise does.
   */
  clientIssueUuid: string;
}

interface RaiseIssuesVars {
  projectId: string;
  issues: BatchIssue[];
  /** Shared by every issue in the batch. */
  description?: string;
  assignedToId?: string | null;
  dueDate?: string | null;
  /** Called each time an issue settles, raised or not, with the running count. */
  onProgress?: (settled: number) => void;
}

/** Per-record outcome of a batch raise. Never rejects on a failed issue. */
export interface RaiseIssuesResult {
  raised: { recordId: string; issue: ProjectIssue }[];
  failed: { recordId: string; error: unknown }[];
}

/**
 * How many raises a batch keeps in flight. Low on purpose: this runs on a
 * jobsite connection, and three at a time finishes a batch of 25 about as fast
 * as ten would without starving the rest of the app. See FE-DEBT-032.
 */
export const BATCH_RAISE_CONCURRENCY = 3;

interface EditIssueVars {
  projectId: string;
  issueId: string;
  title?: string;
  description?: string;
  assignedToId?: string | null;
  dueDate?: string | null;
}

interface PostUpdateVars {
  projectId: string;
  issueId: string;
  status: SelectableIssueStatus;
  remarks?: string;
  /**
   * What this update forecasts, as an ISO date (`yyyy-MM-dd`). Omitted and null
   * mean the same thing — this update says nothing about a target, which is not
   * the same as clearing the last one. An update is never edited, so a forecast
   * is superseded rather than retracted. See [[issues.spec]].
   */
  targetDate?: string | null;
  photos?: File[];
}

interface DeleteIssueVars {
  projectId: string;
  issueId: string;
  /**
   * The issue's record, so the record's own detail query is invalidated too
   * and its issues band drops the row. Callers pass `issue.record`.
   */
  recordId: string;
}

interface IssueVars {
  projectId: string;
  issueId: string;
}

interface IssueContributorVars extends IssueVars {
  /** The person's user id — what the server keys a contributor by. */
  userId: string;
}

type AuthenticatedFetch = ReturnType<typeof useAuthenticatedFetch>;

/**
 * The single retry an idempotent DELETE makes: none on `OfflineError`
 * (nothing to retry against) or any 4xx (the server explicitly refused, so a
 * retry only burns backoff), one otherwise. POSTs deliberately don't use this;
 * see the comment in `main.tsx` on why there is no global mutation retry.
 */
function retryDeleteOnce(failureCount: number, err: Error): boolean {
  if (err instanceof OfflineError) return false;
  if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
    return false;
  }
  return failureCount < 1;
}

/** The one raise request, shared by the single and batch paths. */
function postIssue(
  fetchWithAuth: AuthenticatedFetch,
  {
    projectId,
    recordId,
    title,
    description,
    assignedToId,
    dueDate,
    clientIssueUuid,
  }: RaiseIssueVars,
): Promise<ProjectIssue> {
  return fetchWithAuth<ProjectIssue>(
    `/projects/2.0/projects/${projectId}/issues/`,
    {
      method: "POST",
      body: JSON.stringify({
        record: recordId,
        title,
        description: description ?? "",
        assigned_to_id: assignedToId ?? null,
        due_date: dueDate ?? null,
        ...(clientIssueUuid ? { client_issue_uuid: clientIssueUuid } : {}),
      }),
    },
  );
}

/**
 * Raise an issue against a record.
 *
 * Non-optimistic per [[mutations]]: an issue's number is allocated server-side
 * and is display identity the user will quote, so there is nothing honest to
 * show until the server answers.
 *
 * Invalidates the whole project prefix rather than just the list — the header
 * counts hang off it, and a list that has gained a row above a count that has
 * not is worse than either being briefly stale.
 */
export function useRaiseIssue() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<ProjectIssue, Error, RaiseIssueVars>({
    mutationFn: (vars) => postIssue(fetchWithAuth, vars),
    onSuccess: (_issue, { projectId }) => {
      void queryClient.invalidateQueries({
        queryKey: issueKeys.byProject(projectId),
      });
    },
  });
}

/**
 * Raise one issue per record, all sharing assignee, due date and description.
 *
 * There is no bulk-create endpoint yet (FE-DEBT-032), so this sends one raise
 * per issue, {@link BATCH_RAISE_CONCURRENCY} at a time. It is one mutation over
 * the whole batch rather than a loop of `useRaiseIssue().mutate()`: callbacks
 * handed to `mutate` fire for the last call only, so a loop would lose every
 * result but one.
 *
 * Resolves with a per-record outcome instead of rejecting on the first
 * failure. A partial batch is an ordinary result here — the sheet shows it and
 * retries just the failures, reusing their `clientIssueUuid`s. When the bulk
 * endpoint lands, only the `mutationFn` changes; the result shape stays.
 *
 * Invalidates the project's issues once, at the end, and only if anything was
 * raised.
 */
export function useRaiseIssues() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<RaiseIssuesResult, Error, RaiseIssuesVars>({
    mutationFn: async ({
      projectId,
      issues,
      description,
      assignedToId,
      dueDate,
      onProgress,
    }) => {
      const run = createSemaphore(BATCH_RAISE_CONCURRENCY);
      let settled = 0;

      const outcomes = await Promise.allSettled(
        issues.map((item) =>
          run(() =>
            postIssue(fetchWithAuth, {
              projectId,
              recordId: item.recordId,
              title: item.title,
              description,
              assignedToId,
              dueDate,
              clientIssueUuid: item.clientIssueUuid,
            }),
          ).finally(() => {
            settled += 1;
            onProgress?.(settled);
          }),
        ),
      );

      const result: RaiseIssuesResult = { raised: [], failed: [] };
      outcomes.forEach((outcome, index) => {
        const { recordId } = issues[index];
        if (outcome.status === "fulfilled") {
          result.raised.push({ recordId, issue: outcome.value });
        } else {
          result.failed.push({ recordId, error: outcome.reason });
        }
      });
      return result;
    },
    onSuccess: (result, { projectId }) => {
      if (result.raised.length === 0) return;
      void queryClient.invalidateQueries({
        queryKey: issueKeys.byProject(projectId),
      });
    },
  });
}

/**
 * Edit an issue's title, description, assignee or due date.
 *
 * Status is deliberately absent from the payload — it is read-only on the
 * server too, and the only thing that moves it is posting an update. See
 * [[issues.spec]].
 *
 * Only the fields the caller passes are sent, so a sheet that edits the due
 * date alone does not blank a description it never rendered.
 */
export function useEditIssue() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<ProjectIssue, Error, EditIssueVars>({
    mutationFn: ({ projectId, issueId, ...fields }) => {
      const body: Record<string, unknown> = {};
      if (fields.title !== undefined) body.title = fields.title;
      if (fields.description !== undefined) {
        body.description = fields.description;
      }
      if (fields.assignedToId !== undefined) {
        body.assigned_to_id = fields.assignedToId;
      }
      if (fields.dueDate !== undefined) body.due_date = fields.dueDate;

      return fetchWithAuth<ProjectIssue>(
        `/projects/2.0/projects/${projectId}/issues/${issueId}/`,
        { method: "PATCH", body: JSON.stringify(body) },
      );
    },
    onSuccess: (_issue, { projectId }) => {
      void queryClient.invalidateQueries({
        queryKey: issueKeys.byProject(projectId),
      });
      invalidateReportContents(queryClient, projectId);
    },
  });
}

/**
 * Post an update — the one write path for everything after an issue is raised.
 *
 * Multipart because an update may carry photos. `status` is always sent, even
 * when it matches the status the issue already has: the trail is a complete
 * state log, and the server requires it. See [[issues.spec]].
 *
 * A target date rides along as its own field when the caller has one. It is
 * never written into `remarks`, which hold only what somebody typed — the two
 * have to stay distinguishable for the trail to render a date as a date.
 *
 * A 403 here is a real answer, not a bug — it means this user may not move the
 * issue to that status (closing is gated apart from the rest). Callers surface
 * it as such rather than as a generic failure.
 */
export function usePostIssueUpdate() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<IssueUpdate, Error, PostUpdateVars>({
    mutationFn: ({
      projectId,
      issueId,
      status,
      remarks,
      targetDate,
      photos,
    }) => {
      const form = new FormData();
      form.append("status", status);
      if (remarks) form.append("remarks", remarks);
      // Appended only when there is one. The server reads absent, null and
      // empty as the same silence, so an empty part would say nothing it does
      // not already hear from the omission — leaving it out keeps the request
      // to what this update actually states, the same way `remarks` is handled
      // a line above.
      if (targetDate) form.append("target_date", targetDate);
      for (const photo of photos ?? []) form.append("photos", photo);

      return apiFetchMultipart<IssueUpdate>(
        `/projects/2.0/projects/${projectId}/issues/${issueId}/updates/`,
        form,
        getAccessToken,
      );
    },
    onSuccess: (_update, { projectId }) => {
      void queryClient.invalidateQueries({
        queryKey: issueKeys.byProject(projectId),
      });
      invalidateReportContents(queryClient, projectId);
    },
  });
}

/**
 * Delete an issue. A soft delete server-side — the row is hidden from every
 * user surface, and support can restore it. See [[issues.spec]] § Deleting an
 * issue and the 2026-09-04 issue-delete design.
 *
 * Not optimistic, and online-only in this wave: no outbox entry, the same
 * rule as every other issue write.
 *
 * DELETE is idempotent, so this opts into the same single retry
 * `useDeleteRecord` makes: none on `OfflineError` (nothing to retry against)
 * or any 4xx (the server explicitly rejected; a retry only burns backoff),
 * one retry otherwise. A 404 resolves as success — the issue is already gone,
 * which is exactly what a retry after a lost response sees. POSTs deliberately
 * do NOT inherit this; see the comment in `main.tsx` on why there is no global
 * mutation retry.
 *
 * Invalidates the whole project prefix (list, detail and counts together, as
 * every issue mutation does) and the record's detail, so the record's issues
 * band drops the row. Any report holding the issue turns its row into a
 * tombstone and recounts.
 */
export function useDeleteIssue() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteIssueVars>({
    retry: retryDeleteOnce,
    mutationFn: async ({ projectId, issueId }) => {
      try {
        await fetchWithAuth<void>(
          `/projects/2.0/projects/${projectId}/issues/${issueId}/`,
          { method: "DELETE" },
        );
      } catch (err) {
        // 404 = already gone → success for an idempotent DELETE.
        if (err instanceof ApiError && err.status === 404) return;
        throw err;
      }
    },
    onSuccess: (_data, { projectId, recordId }) => {
      void queryClient.invalidateQueries({
        queryKey: issueKeys.byProject(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: recordKeys.detail(projectId, recordId),
      });
      invalidateReportContents(queryClient, projectId, { counts: true });
    },
  });
}

/**
 * Make a fresh link onto an issue, to send on WhatsApp.
 *
 * Every send makes its own link, and it belongs to the caller: everyone who
 * joins through it counts as invited by them. Nothing lists links, so a success
 * changes nothing on screen and invalidates nothing. A 403 means the caller can
 * no longer bring people on — the issue closed, or their access changed — so
 * the issue refetches and takes the button away. See
 * [[add-contributor-sheet.spec]].
 *
 * No retry: a retry after a lost response would only make a second link, and
 * the sender is right there to tap again.
 */
export function useCreateIssueInviteLink() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<IssueInviteLink, Error, IssueVars>({
    mutationFn: ({ projectId, issueId }) =>
      fetchWithAuth<IssueInviteLink>(
        `/projects/2.0/projects/${projectId}/issues/${issueId}/invite-links/`,
        { method: "POST" },
      ),
    onError: (err, { projectId, issueId }) => {
      if (err instanceof ApiError && err.status === 403) {
        void queryClient.invalidateQueries({
          queryKey: issueKeys.detail(projectId, issueId),
        });
      }
    },
  });
}

/**
 * Put someone already on the project onto an issue, from the project's member
 * list. See [[contributor-picker.spec]].
 *
 * Adding someone already on the issue is answered with their existing row, so a
 * double tap or a lost response is harmless. Only the issue's detail changes —
 * its contributor list; the caller's list rows and counts stay the same — so
 * only it refetches, success or not: a refusal means the caller's access or the
 * issue moved under them. A 400 is the person no longer being on the project,
 * so the project refetches too.
 */
export function useAddIssueContributor() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<IssueContributor, Error, IssueContributorVars>({
    mutationFn: ({ projectId, issueId, userId }) =>
      fetchWithAuth<IssueContributor>(
        `/projects/2.0/projects/${projectId}/issues/${issueId}/contributors/`,
        { method: "POST", body: JSON.stringify({ user_id: userId }) },
      ),
    onSettled: (_row, err, { projectId, issueId }) => {
      void queryClient.invalidateQueries({
        queryKey: issueKeys.detail(projectId, issueId),
      });
      if (err instanceof ApiError && err.status === 400) {
        void queryClient.invalidateQueries({
          queryKey: projectKeys.detail(projectId),
        });
      }
    },
  });
}

/**
 * Take someone off an issue. See [[issue-contributors.spec]].
 *
 * DELETE is idempotent, so it makes the same single retry `useDeleteIssue`
 * does, and a 404 resolves as success: they're already off, which is exactly
 * what a retry after a lost response sees. The issue refetches either way, so
 * the row goes on success and, on a 403, so does the Remove the caller no
 * longer holds.
 */
export function useRemoveIssueContributor() {
  const fetchWithAuth = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation<void, Error, IssueContributorVars>({
    retry: retryDeleteOnce,
    mutationFn: async ({ projectId, issueId, userId }) => {
      try {
        await fetchWithAuth<void>(
          `/projects/2.0/projects/${projectId}/issues/${issueId}/contributors/${userId}/`,
          { method: "DELETE" },
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return;
        throw err;
      }
    },
    onSettled: (_data, _err, { projectId, issueId }) => {
      void queryClient.invalidateQueries({
        queryKey: issueKeys.detail(projectId, issueId),
      });
    },
  });
}
